import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Mail, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('confirming'); // confirming, success, error, invalid
  const [processing, setProcessing] = useState(false);
  
  const email = searchParams.get('email');
  const token = searchParams.get('token');

  useEffect(() => {
    if (!email || !token) {
      setStatus('invalid');
    }
  }, [email, token]);

  const handleUnsubscribe = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/unsubscribe/confirm?email=${encodeURIComponent(email)}&token=${token}`);
      setStatus('success');
    } catch (error) {
      setStatus('error');
    } finally {
      setProcessing(false);
    }
  };

  if (status === 'invalid') {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'var(--bg-primary)',
        padding: '20px'
      }}>
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
          padding: '40px',
          maxWidth: '500px',
          width: '100%',
          textAlign: 'center'
        }}>
          <XCircle size={64} style={{ color: 'var(--accent-red)', marginBottom: '20px' }} />
          <h1 style={{ color: 'var(--accent-silver)', fontFamily: 'var(--font-serif)', marginBottom: '15px' }}>
            Invalid Link
          </h1>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '30px' }}>
            This unsubscribe link is invalid or has expired.
          </p>
          <Button className="btn-primary" asChild>
            <Link to="/">Return to Website</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'var(--bg-primary)',
        padding: '20px'
      }}>
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
          padding: '40px',
          maxWidth: '500px',
          width: '100%',
          textAlign: 'center'
        }}>
          <CheckCircle size={64} style={{ color: 'var(--accent-gold)', marginBottom: '20px' }} />
          <h1 style={{ color: 'var(--accent-silver)', fontFamily: 'var(--font-serif)', marginBottom: '15px' }}>
            Successfully Unsubscribed
          </h1>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '30px' }}>
            You have been removed from our newsletter. We're sorry to see you go!
          </p>
          <Button className="btn-primary" asChild>
            <Link to="/">Return to Website</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'var(--bg-primary)',
        padding: '20px'
      }}>
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
          padding: '40px',
          maxWidth: '500px',
          width: '100%',
          textAlign: 'center'
        }}>
          <XCircle size={64} style={{ color: 'var(--accent-red)', marginBottom: '20px' }} />
          <h1 style={{ color: 'var(--accent-silver)', fontFamily: 'var(--font-serif)', marginBottom: '15px' }}>
            Something Went Wrong
          </h1>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '30px' }}>
            We couldn't process your unsubscribe request. Please try again or contact us.
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
            <Button className="btn-secondary" onClick={() => setStatus('confirming')}>
              Try Again
            </Button>
            <Button className="btn-primary" asChild>
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Confirming state
  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: 'var(--bg-primary)',
      padding: '20px'
    }}>
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border-color)',
        borderRadius: '8px',
        padding: '40px',
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center'
      }}>
        <Mail size={64} style={{ color: 'var(--accent-silver)', marginBottom: '20px' }} />
        <h1 style={{ color: 'var(--accent-silver)', fontFamily: 'var(--font-serif)', marginBottom: '15px' }}>
          Unsubscribe
        </h1>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '10px' }}>
          Are you sure you want to unsubscribe
        </p>
        <p style={{ color: 'var(--accent-gold)', fontWeight: 'bold', marginBottom: '30px' }}>
          {email}
        </p>
        <p style={{ color: 'var(--text-muted)', marginBottom: '30px', fontSize: '0.9rem' }}>
          You will no longer receive newsletters and updates from Heeloly Upasani.
        </p>
        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
          <Button className="btn-secondary" asChild>
            <Link to="/">Cancel</Link>
          </Button>
          <Button 
            className="btn-primary" 
            onClick={handleUnsubscribe}
            disabled={processing}
            style={{ background: 'var(--accent-red)' }}
          >
            {processing ? 'Processing...' : 'Yes, Unsubscribe'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Unsubscribe;
